import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { format, parseISO, startOfWeek, endOfWeek, isWithinInterval } from 'date-fns';
import {
  Phone, MessageSquare, Mail, Users, X, Plus, Send, Search,
  Calendar, Clock, User, FileText, ChevronDown, Filter
} from 'lucide-react';
import './CommunicationLogger.css';

const COMM_TYPES = [
  { value: 'call', label: 'Phone Call', icon: Phone },
  { value: 'text', label: 'Text', icon: MessageSquare },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'in_person', label: 'In Person', icon: Users }
];

function CommunicationLogger() {
  const { state, addCommunicationLog } = useApp();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  // Form state
  const [clientId, setClientId] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [communicationType, setCommunicationType] = useState('call');
  const [commDate, setCommDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [commTime, setCommTime] = useState(format(new Date(), 'HH:mm'));
  const [note, setNote] = useState('');

  // Report filter state
  const [reportWeekStart, setReportWeekStart] = useState(
    format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
  );

  const activeClients = useMemo(() => {
    return (state.clients || [])
      .filter(c => c.status !== 'deleted')
      .sort((a, b) => {
        const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
        const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [state.clients]);

  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return activeClients.slice(0, 10);
    const q = clientSearch.toLowerCase();
    return activeClients.filter(c => {
      const name = `${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase();
      const phone = (c.phone || '').replace(/\D/g, '');
      const searchPhone = q.replace(/\D/g, '');
      return name.includes(q) || (searchPhone && phone.includes(searchPhone));
    }).slice(0, 10);
  }, [activeClients, clientSearch]);

  const selectedClient = useMemo(() => {
    if (!clientId) return null;
    return state.clients?.find(c => c.id === clientId);
  }, [clientId, state.clients]);

  // Weekly report data
  const weeklyLogs = useMemo(() => {
    const weekStart = parseISO(reportWeekStart);
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
    return (state.communicationLogs || [])
      .filter(log => {
        const logDate = parseISO(log.communicationDate || log.createdAt);
        return isWithinInterval(logDate, { start: weekStart, end: weekEnd });
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [state.communicationLogs, reportWeekStart]);

  const resetForm = () => {
    setClientId('');
    setClientSearch('');
    setCommunicationType('call');
    setCommDate(format(new Date(), 'yyyy-MM-dd'));
    setCommTime(format(new Date(), 'HH:mm'));
    setNote('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSaving(true);
    try {
      const clientName = selectedClient
        ? `${selectedClient.firstName || ''} ${selectedClient.lastName || ''}`.trim()
        : 'OTHER';
      await addCommunicationLog({
        clientId: clientId || null,
        clientName,
        communicationType,
        communicationDate: commDate,
        communicationTime: commTime,
        note: note.trim()
      });
      setSuccessMsg('Communication logged!');
      resetForm();
      setTimeout(() => setSuccessMsg(''), 2000);
    } catch (err) {
      console.error('Failed to log communication:', err);
      alert('Failed to save. Please try again.');
    }
    setSaving(false);
  };

  const selectClient = (client) => {
    setClientId(client.id);
    setClientSearch(`${client.firstName || ''} ${client.lastName || ''}`.trim());
    setShowClientDropdown(false);
  };

  const setOther = () => {
    setClientId('');
    setClientSearch('OTHER');
    setShowClientDropdown(false);
  };

  const getTypeIcon = (type) => {
    const t = COMM_TYPES.find(ct => ct.value === type);
    return t ? t.icon : Phone;
  };

  const getTypeLabel = (type) => {
    const t = COMM_TYPES.find(ct => ct.value === type);
    return t ? t.label : type;
  };

  // Group weekly logs by day
  const logsByDay = useMemo(() => {
    const groups = {};
    weeklyLogs.forEach(log => {
      const day = log.communicationDate || format(parseISO(log.createdAt), 'yyyy-MM-dd');
      if (!groups[day]) groups[day] = [];
      groups[day].push(log);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a));
  }, [weeklyLogs]);

  if (!isOpen) {
    return (
      <button className="comm-log-fab" onClick={() => setIsOpen(true)} title="Log Communication">
        <Phone size={22} />
        <span className="comm-log-fab-label">Log Comm</span>
      </button>
    );
  }

  return (
    <div className="comm-log-panel">
      <div className="comm-log-header">
        <div className="comm-log-tabs">
          <button
            className={`comm-log-tab ${!showReport ? 'active' : ''}`}
            onClick={() => setShowReport(false)}
          >
            <Plus size={14} /> New Log
          </button>
          <button
            className={`comm-log-tab ${showReport ? 'active' : ''}`}
            onClick={() => setShowReport(true)}
          >
            <FileText size={14} /> Weekly Report
          </button>
        </div>
        <button className="comm-log-close" onClick={() => { setIsOpen(false); setShowReport(false); }}>
          <X size={18} />
        </button>
      </div>

      {!showReport ? (
        <form className="comm-log-form" onSubmit={handleSubmit}>
          {successMsg && (
            <div className="comm-log-success">{successMsg}</div>
          )}

          {/* Client selector */}
          <div className="comm-field">
            <label><User size={14} /> Client</label>
            <div className="comm-client-search">
              <Search size={14} className="comm-search-icon" />
              <input
                type="text"
                value={clientSearch}
                onChange={(e) => {
                  setClientSearch(e.target.value);
                  setClientId('');
                  setShowClientDropdown(true);
                }}
                onFocus={() => setShowClientDropdown(true)}
                placeholder="Search client or type OTHER..."
              />
              {showClientDropdown && (
                <div className="comm-client-dropdown">
                  <div className="comm-client-option other" onClick={setOther}>
                    <span className="comm-client-other-badge">OTHER</span>
                    <span>Not tied to a client</span>
                  </div>
                  {filteredClients.map(c => (
                    <div key={c.id} className="comm-client-option" onClick={() => selectClient(c)}>
                      <span className="comm-client-name">
                        {c.firstName || ''} {c.lastName || ''}
                      </span>
                      {c.phone && <span className="comm-client-phone">{c.phone}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Communication type */}
          <div className="comm-field">
            <label><MessageSquare size={14} /> Type</label>
            <div className="comm-type-buttons">
              {COMM_TYPES.map(t => (
                <button
                  key={t.value}
                  type="button"
                  className={`comm-type-btn ${communicationType === t.value ? 'active' : ''}`}
                  onClick={() => setCommunicationType(t.value)}
                >
                  <t.icon size={14} />
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Date & Time */}
          <div className="comm-field-row">
            <div className="comm-field">
              <label><Calendar size={14} /> Date Received</label>
              <input
                type="date"
                value={commDate}
                onChange={(e) => setCommDate(e.target.value)}
              />
            </div>
            <div className="comm-field">
              <label><Clock size={14} /> Time Received</label>
              <input
                type="time"
                value={commTime}
                onChange={(e) => setCommTime(e.target.value)}
              />
            </div>
          </div>

          {/* Note */}
          <div className="comm-field">
            <label><FileText size={14} /> Notes</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What was the communication about?"
              rows={3}
              required
            />
          </div>

          <button type="submit" className="comm-submit-btn" disabled={saving || !note.trim()}>
            <Send size={14} />
            {saving ? 'Saving...' : 'Log Communication'}
          </button>
        </form>
      ) : (
        <div className="comm-report">
          <div className="comm-report-filter">
            <label>Week of:</label>
            <input
              type="date"
              value={reportWeekStart}
              onChange={(e) => setReportWeekStart(e.target.value)}
            />
            <span className="comm-report-count">{weeklyLogs.length} entries</span>
          </div>

          <div className="comm-report-list">
            {logsByDay.length === 0 ? (
              <div className="comm-report-empty">
                <FileText size={32} />
                <p>No communications logged this week</p>
              </div>
            ) : (
              logsByDay.map(([day, logs]) => (
                <div key={day} className="comm-report-day">
                  <div className="comm-report-day-header">
                    <span>{format(parseISO(day), 'EEEE, MMM d')}</span>
                    <span className="comm-report-day-count">{logs.length}</span>
                  </div>
                  {logs.map(log => {
                    const TypeIcon = getTypeIcon(log.communicationType);
                    return (
                      <div key={log.id} className="comm-report-item">
                        <div className={`comm-report-type-icon ${log.communicationType}`}>
                          <TypeIcon size={14} />
                        </div>
                        <div className="comm-report-item-content">
                          <div className="comm-report-item-header">
                            <span className="comm-report-client">
                              {log.clientName || 'OTHER'}
                            </span>
                            <span className="comm-report-time">
                              {log.communicationTime || '--:--'}
                            </span>
                          </div>
                          <p className="comm-report-note">{log.note}</p>
                          <div className="comm-report-meta">
                            <span>{getTypeLabel(log.communicationType)}</span>
                            <span>Logged by {log.recordedByName}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CommunicationLogger;
